import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { NavigateFunction } from 'react-router-dom';
import { useGetTemplateFunctionsQuery } from 'store/services/helios/templateFunctions';
import { getRowId } from 'components/common/Table/tableFunctions';
import { getLocalizedHeliosFunctionAlias, getCurrentLanguageContent } from 'utils/helios/language';
import { sortHeliosFunctionsByOrder } from 'utils/helios/functions';
import { isMobileApp } from 'utils/common';
import type { HeliosRecordDataTablesRes, HeliosRecordDataTablesData, HeliosRecordFunction } from 'types/dataHelios';
import type { HeliosColumnsCellData } from 'types/helios/agendaListing';
import type { SaveAllRowsResult } from 'types/helios/record';
import type { TableRef } from 'components/common/Table/Table';
import { buildListingFunctionPayload, type HeliosFunctionContext } from 'utils/helios/buildHeliosFunctionPayload';
import { useHeliosFunctionRunner } from './useHeliosFunctionRunner';
import type { FunctionModalConfirmData } from 'components/user/DummyHelios/Functions/modals/FunctionModal';

interface UseHeliosFunctionsProps {
  datasourceId: string;
  recordsByRecordNumber: Record<string, HeliosRecordDataTablesData> | undefined;
  rowDataByIdentifier: Record<string, HeliosRecordDataTablesData>;
  selectedRows: string[];
  visibleSelectedRows: string[];
  data: HeliosRecordDataTablesRes | undefined;
  refetch: (result?: SaveAllRowsResult) => void;
  templateViewId: string;
  moduleId?: string;
  navigate: NavigateFunction;
  transformedData: HeliosColumnsCellData[];
  tableRef: React.MutableRefObject<TableRef | null>;
}

export const useHeliosFunctions = ({
  datasourceId,
  recordsByRecordNumber,
  rowDataByIdentifier,
  selectedRows,
  visibleSelectedRows,
  data,
  refetch,
  templateViewId,
  moduleId,
  navigate,
  transformedData,
  tableRef,
}: UseHeliosFunctionsProps) => {
  const { i18n } = useTranslation();
  const mobileApp = isMobileApp();

  const { data: templateFunctions } = useGetTemplateFunctionsQuery({ id: templateViewId }, { skip: !templateViewId });

  const buildPayload = useCallback(
    (args: { context: HeliosFunctionContext; request?: FunctionModalConfirmData['request'] }) =>
      buildListingFunctionPayload({
        ...args,
        datasourceId,
        recordsByRecordNumber,
        rowDataByIdentifier,
        visibleSelectedRows,
      }),
    [datasourceId, recordsByRecordNumber, rowDataByIdentifier, visibleSelectedRows]
  );

  const handleSuccess = useCallback(() => {
    tableRef.current?.clearSelection();
    refetch();
  }, [refetch, tableRef]);

  const { functionRef, runFunction, handleHeliosFunction, isFunctionRunning, functionModals, openFunctionModal, closeFunctionModal } =
    useHeliosFunctionRunner({
      templateViewId,
      moduleId,
      navigate,
      buildPayload,
      onSuccess: handleSuccess,
      clearFunctionRefDelay: 100,
    });

  const templateFunctionsById = useMemo(() => {
    return new Map((templateFunctions ?? []).map((fn) => [fn.id, fn]));
  }, [templateFunctions]);

  const functionsOnRows: Record<string, string[]> = useMemo(() => {
    if (!data) return {};

    return data.data.reduce<Record<string, string[]>>((acc, item) => {
      if (item.recordNumber == null) return acc;
      acc[item.recordNumber.toString()] = item.functions.map((f) => f.id);
      return acc;
    }, {});
  }, [data]);

  const functionsOnHeader = useMemo(() => {
    if (!data?.functions || !selectedRows.length || !data?.data) {
      return data?.functions?.filter((fn) => fn.functionType === 'Heg') ?? [];
    }

    const selectedRowFunctionIds = data.data
      .filter((row) => row.recordNumber != null && selectedRows.some((r) => getRowId(r) === row.recordNumber.toString()))
      .flatMap((row) => row.functions.map((f) => f.id));

    const hegFunctions = data.functions.filter((fn) => fn.functionType === 'Heg');

    const hegWorkflowFunctions = data.functions.filter((fn) => fn.functionType === 'HegWorkflow' && selectedRowFunctionIds.includes(fn.id));

    const allFunctions = [...hegFunctions, ...hegWorkflowFunctions];
    const uniqueFunctions = allFunctions.filter((fn, idx, arr) => arr.findIndex((f) => f.id === fn.id) === idx);

    return uniqueFunctions;
  }, [data?.functions, data?.data, selectedRows]);

  const heliosRowFunctions = useMemo(
    () =>
      transformedData
        .map((row) => {
          const dataRow = recordsByRecordNumber?.[row.id];
          if (!dataRow || dataRow?.functions.length === 0) return null;
          return {
            rowId: row.id,
            controls: sortHeliosFunctionsByOrder(dataRow.functions, (func) => templateFunctionsById.get(func.id)?.order ?? func.order).map(
              (func: HeliosRecordFunction) => ({
                id: func.id,
                alias: getLocalizedHeliosFunctionAlias(i18n, templateFunctionsById.get(func.id) ?? func, mobileApp),
                tooltip:
                  getCurrentLanguageContent(i18n, templateFunctionsById.get(func.id)?.tooltipTranslations ?? func.tooltipTranslations) ||
                  func.tooltip,
                iconName: mobileApp
                  ? templateFunctionsById.get(func.id)?.mobileIconName ||
                    templateFunctionsById.get(func.id)?.iconName ||
                    func.mobileIconName ||
                    func.iconName
                  : templateFunctionsById.get(func.id)?.iconName || func.iconName,
                color: mobileApp
                  ? templateFunctionsById.get(func.id)?.mobileColor || templateFunctionsById.get(func.id)?.color || func.mobileColor || func.color
                  : templateFunctionsById.get(func.id)?.color || func.color,
                onClick: () => {
                  handleHeliosFunction({ func, rowId: row.id });
                },
              })
            ),
          };
        })
        .filter(
          (
            item
          ): item is {
            rowId: string;
            controls: {
              id: string;
              alias: string;
              tooltip: string;
              iconName: string;
              color: string;
              onClick: () => void;
            }[];
          } => item !== null
        ),
    [recordsByRecordNumber, transformedData, handleHeliosFunction, i18n, mobileApp, templateFunctionsById]
  );

  return {
    functionRef,
    runFunction,
    handleHeliosFunction,
    functionsOnHeader,
    functionsOnRows,
    heliosRowFunctions,
    isFunctionRunning,
    functionModals,
    openFunctionModal,
    closeFunctionModal,
  };
};
